import * as api from "../api/disciplio-api";
import {
  deleteWeeklyGoal,
  dismissSuggestion,
  getTodayKey,
  touchState,
  updateWeeklyGoal,
} from "../disciplio/logic";
import { settingsWithMetricUnits } from "../disciplio/metric-units";
import { settingsWithWeeklyGoalCategories } from "../disciplio/weekly-goal-categories";
import type {
  AppState,
  DailyItem,
  DisciplioSettings,
  MonthlyGoal,
  RagSource,
  UserProfile,
  WeeklyGoal,
} from "../disciplio/types";

export type DailyPatch = Partial<
  Pick<
    DailyItem,
    | "title"
    | "type"
    | "time"
    | "weeklyGoalId"
    | "contributionAmount"
    | "contributionUnit"
    | "repeat"
    | "done"
    | "timeConstraintType"
  >
>;

export type DailyUpdateOutcome = "saved" | "calendar-habit-time-scope-required" | "failed";

export type DailyItemMove = {
  itemId: string;
  fromDateKey: string;
  toDateKey: string;
  time: string;
};

export type Action =
  | { type: "hydrateProfile"; profile: UserProfile }
  | { type: "saveSettings"; settings: DisciplioSettings }
  | { type: "saveProfile"; profile: UserProfile }
  | { type: "feedback"; text: string }
  | { type: "replaceDailyItems"; dateKey: string; items: DailyItem[] }
  | { type: "addDailyItem"; dateKey: string; item: DailyItem }
  | { type: "updateDailyItem"; dateKey: string; id: string; patch: DailyPatch }
  | { type: "deleteDailyItem"; dateKey: string; id: string }
  | { type: "replaceDailyItemId"; dateKey: string; localId: string; remoteId: string }
  | { type: "moveDailyItem"; fromDateKey: string; toDateKey: string; itemId: string; patch?: DailyPatch }
  | { type: "replaceCompletionHistory"; history: Record<string, boolean> }
  | { type: "dismissSuggestion"; id: string }
  | { type: "addMonthlyGoal"; goal: MonthlyGoal }
  | { type: "replaceMonthlyGoals"; goals: MonthlyGoal[] }
  | { type: "updateMonthlyGoal"; id: string; patch: Partial<Pick<MonthlyGoal, "title" | "target" | "unit" | "progress" | "yearGoal" | "yearGoalId" | "completed">> }
  | { type: "deleteMonthlyGoal"; id: string }
  | { type: "addWeeklyGoal"; goal: WeeklyGoal }
  | { type: "replaceWeeklyGoals"; goals: WeeklyGoal[] }
  | { type: "updateWeeklyGoal"; id: string; patch: Partial<Pick<WeeklyGoal, "title" | "target" | "unit" | "progress" | "category" | "completed">> }
  | { type: "deleteWeeklyGoal"; id: string }
  | { type: "replaceRagSources"; sources: RagSource[] }
  | { type: "addRagSource"; source: RagSource }
  | { type: "updateRagSource"; id: string; patch: Partial<RagSource> }
  | { type: "deleteRagSource"; id: string };

function itemsForDate(state: AppState, dateKey: string): DailyItem[] {
  return state.dailyItemsByDate[dateKey] ?? [];
}

function withDateItems(state: AppState, dateKey: string, items: DailyItem[]): AppState {
  const nextByDate = { ...state.dailyItemsByDate, [dateKey]: items };
  const todayKey = getTodayKey(state.settings);
  return touchState({
    ...state,
    dailyItemsByDate: nextByDate,
    dailyItems: nextByDate[todayKey] ?? [],
  });
}

function mergeDailyPlanItems(current: DailyItem[], incoming: DailyItem[]): DailyItem[] {
  const incomingIds = new Set(incoming.map((item) => item.id));
  const optimisticItems = current.filter((item) => item.id.startsWith("d-") && !incomingIds.has(item.id));
  return [...incoming, ...optimisticItems];
}

export function reducer(state: AppState, action: Action): AppState {
  switch (action.type) {
    case "hydrateProfile":
      return touchState({ ...state, profile: action.profile });
    case "saveSettings": {
      const settings = settingsWithWeeklyGoalCategories(settingsWithMetricUnits(action.settings));
      const next = { ...state, settings };
      const todayKey = getTodayKey(settings);
      return touchState({
        ...next,
        dailyItems: next.dailyItemsByDate[todayKey] ?? [],
      });
    }
    case "saveProfile":
      return touchState({ ...state, profile: action.profile });
    case "feedback":
      return touchState({ ...state, userFeedback: action.text });
    case "replaceDailyItems":
      return withDateItems(state, action.dateKey, mergeDailyPlanItems(itemsForDate(state, action.dateKey), action.items));
    case "addDailyItem":
      return withDateItems(state, action.dateKey, [...itemsForDate(state, action.dateKey), action.item]);
    case "updateDailyItem":
      return withDateItems(
        state,
        action.dateKey,
        itemsForDate(state, action.dateKey).map((item) => (item.id === action.id ? { ...item, ...action.patch } : item))
      );
    case "deleteDailyItem":
      return withDateItems(
        state,
        action.dateKey,
        itemsForDate(state, action.dateKey).filter((item) => item.id !== action.id)
      );
    case "replaceDailyItemId":
      return withDateItems(
        state,
        action.dateKey,
        itemsForDate(state, action.dateKey).map((item) =>
          item.id === action.localId ? { ...item, id: action.remoteId } : item
        )
      );
    case "moveDailyItem": {
      const fromItems = itemsForDate(state, action.fromDateKey);
      const moving = fromItems.find((item) => item.id === action.itemId);
      if (!moving) return state;
      const nextItem = action.patch ? { ...moving, ...action.patch } : moving;
      const fromNext = fromItems.filter((item) => item.id !== action.itemId);
      const toItems = itemsForDate(state, action.toDateKey);
      const toNext = [...toItems, nextItem];
      let next = withDateItems(state, action.fromDateKey, fromNext);
      next = withDateItems(next, action.toDateKey, toNext);
      return next;
    }
    case "replaceCompletionHistory":
      return touchState({ ...state, completionHistory: action.history });
    case "dismissSuggestion":
      return dismissSuggestion(state, action.id);
    case "addMonthlyGoal":
      return touchState({ ...state, monthlyGoals: [...state.monthlyGoals, action.goal] });
    case "replaceMonthlyGoals":
      return touchState({ ...state, monthlyGoals: action.goals });
    case "updateMonthlyGoal":
      return touchState({
        ...state,
        monthlyGoals: state.monthlyGoals.map((goal) => (goal.id === action.id ? { ...goal, ...action.patch } : goal)),
      });
    case "deleteMonthlyGoal":
      return touchState({ ...state, monthlyGoals: state.monthlyGoals.filter((goal) => goal.id !== action.id) });
    case "addWeeklyGoal":
      return touchState({ ...state, weeklyGoals: [...state.weeklyGoals, action.goal] });
    case "replaceWeeklyGoals":
      return touchState({ ...state, weeklyGoals: action.goals });
    case "updateWeeklyGoal":
      return updateWeeklyGoal(state, action.id, action.patch);
    case "deleteWeeklyGoal":
      return deleteWeeklyGoal(state, action.id);
    case "replaceRagSources":
      return touchState({ ...state, ragSources: action.sources });
    case "addRagSource":
      return touchState({ ...state, ragSources: [...state.ragSources, action.source] });
    case "updateRagSource":
      return touchState({
        ...state,
        ragSources: state.ragSources.map((source) => (source.id === action.id ? { ...source, ...action.patch } : source)),
      });
    case "deleteRagSource":
      return touchState({ ...state, ragSources: state.ragSources.filter((source) => source.id !== action.id) });
    default:
      return state;
  }
}

export function persistCurrentMonth(goals: MonthlyGoal[], dispatch: (action: Action) => void) {
  const now = new Date();
  api
    .saveMonthlyPlan(now.getFullYear(), now.getMonth() + 1, goals)
    .then((savedGoals) => dispatch({ type: "replaceMonthlyGoals", goals: savedGoals }))
    .catch(() => undefined);
}

export function persistWeek(weekStartDate: string, goals: WeeklyGoal[], dispatch: (action: Action) => void, onSaved?: () => Promise<void>) {
  const goalsForWeek = goals.filter((goal) => (goal.weekStartDate ?? api.currentWeekStartKey()) === weekStartDate);
  api
    .saveWeeklyPlan(weekStartDate, goalsForWeek)
    .then((savedGoals) => {
      const otherWeeks = goals.filter((goal) => (goal.weekStartDate ?? api.currentWeekStartKey()) !== weekStartDate);
      dispatch({ type: "replaceWeeklyGoals", goals: [...otherWeeks, ...savedGoals] });
      return onSaved?.();
    })
    .catch(() => undefined);
}
